import { RestApiErrorResponse } from '@tableau/vizportalclient-common-components/lib/src/http/RestApiService';
import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { CreateBroadcastView, DeleteBroadcastView, GetBroadcastDetail } from '~src/bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { BroadcastErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ILongRunningAction } from '~src/permissions/services/types/ILongRunningAction';
import { IBroadcastableItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';

import { broadcastViewCreated, broadcastViewDeleted, CreateBroadcastViewAction, DeleteBroadcastViewAction, GetBroadcastDetailAction, updateBroadcastView } from '../actions/BroadcastActions';
import { IBroadcastDetail, IGetDetailBroadcastResult } from '../models/BroadcastModels';
import { Broadcasts } from '../resources/Broadcasts';

export function* rootBroadcastSaga(): SagaIterator {
    yield takeEvery(CreateBroadcastView, handleCreateBroadcastView);
    yield takeEvery(DeleteBroadcastView, handleDeleteBroadcastView);
    yield takeEvery(GetBroadcastDetail, processGetBroadcastDetail);
}

export function* handleCreateBroadcastView(action: CreateBroadcastViewAction) {
    try {
        // asynchronous process of publishing a broadcast
        // 1. create the broadcast
        yield call(createBroadcast, action.item);
        yield put(broadcastViewCreated());
        try {
            // 2. fetch attributes of the broadcast such as embed code and public-facing URL
            const result = yield call(getBroadcastView, action.item);
            yield put(updateBroadcastView(result));
        } catch (e) {
            yield put(requestFailed(e, Messages.broadcastUnexpectedError()));
        }
    } catch (e) {
        const localizedMessage = getErrorMessage(e);
        yield put(requestFailed(e, localizedMessage));
    }
}

export function* handleDeleteBroadcastView(action: DeleteBroadcastViewAction) {
    try {
        yield call(deleteBroadcast, action.item);
        yield put(broadcastViewDeleted());
    } catch (e) {
        yield put(requestFailed(e, getErrorMessage(e)));
    }
}

export function* processGetBroadcastDetail(action: GetBroadcastDetailAction) {
    try {
        const result = yield call(getBroadcastView, action.item);
        yield put(updateBroadcastView(result));
    } catch (e) {
        const { code } = extractErrorCodeAndMetaData(e);
        if (code === BroadcastErrorCode.NotFound ||
            code === BroadcastErrorCode.RestNotFound ||
            // TODO: cleanup restapi response
            code === 404) {
            // update states for the initial load of broadcast
            yield put(updateBroadcastView());
        } else {
            // otherwise pop up error toast
            yield put(requestFailed(e, Messages.broadcastUnexpectedError()));
        }
    }
}

export function createBroadcast(item: IBroadcastableItemInfo): Promise<any> {
    return Broadcasts.createBroadcast(item.id, item.luid, false /*showTabs*/, false /*showWatermark*/, true /*overwrite*/, true /*acceptTermsOfUse*/)
        .then(onComplete);
}

export function deleteBroadcast(item: IBroadcastableItemInfo): Promise<any> {
    return Broadcasts.deleteBroadcast(item.luid)
        .then((result: IResult) => {
            if (result?.errors?.length) {
                return onFail(result.errors);
            }
            return Promise.resolve(result);
        });
}

export function getBroadcastView(item: IBroadcastableItemInfo): Promise<IBroadcastDetail> {
    return Broadcasts.getDetailOfABroadcast(item.luid)
        .then((result: IGetDetailBroadcastResult) => {
            if (result?.errors?.length) {
                return onFail(result.errors);
            }
            return Promise.resolve(result as IBroadcastDetail);
        });
}

function onFail(errors: IError[]): Promise<any> {
    return Promise.reject(errors[0]);
}

function onComplete(result: ILongRunningAction): Promise<any> {
    // webclient API does not reject a promise when request fails, 
    // so throwing an exception if response has errors field populated
    if (result?.response?.result?.errors) {
        return onFail(result.response.result.errors);
    }
}

export function getErrorMessage(error: IError | RestApiErrorResponse): string {
    if (!error) {
        return Messages.unexpectedError();
    }

    const { code, limit } = extractErrorCodeAndMetaData(error);

    switch (code) {
        case BroadcastErrorCode.ErrorUnknown:
            return Messages.broadcastError();
        case BroadcastErrorCode.WorkbookDataSourcesAreNotExtracts:
        case BroadcastErrorCode.RestWorkbookDataSourcesAreNotExtracts:
            return Messages.broadcastWorkbookDataSourcesAreNotExtracts();
        case BroadcastErrorCode.WorkbookRowLimitExceeded:
        case BroadcastErrorCode.RestWorkbookRowLimitExceeded:
            return Messages.broadcastWorkbookRowLimitExceeded({ ROW_LIMIT: limit });
        case BroadcastErrorCode.WorkbookSizeLimitExceeded:
        case BroadcastErrorCode.RestWorkbookSizeLimitExceeded:
            return Messages.broadcastWorkbookSizeLimitExceeded({ SIZE_LIMIT: limit });
        case BroadcastErrorCode.WorkbookContainsUserFilter:
        case BroadcastErrorCode.RestWorkbookContainsUserFilter:
            return Messages.broadcastWorkbookContainsUserFilter();
        case BroadcastErrorCode.WorkbookContainsCustomSQL:
        case BroadcastErrorCode.RestWorkbookContainsCustomSQL:
            return Messages.broadcastWorkbookContainsCustomSQL();
        case BroadcastErrorCode.WorkbookContainsCube:
        case BroadcastErrorCode.RestWorkbookContainsCube:
            return Messages.broadcastWorkbookContainsCube();
        case BroadcastErrorCode.WorkbookContainsScript:
        case BroadcastErrorCode.RestWorkbookContainsScript:
            return Messages.broadcastWorkbookContainsScript();
        case BroadcastErrorCode.WorkbookUnknownValidationError:
        case BroadcastErrorCode.RestWorkbookUnknownValidationError:
            return Messages.broadcastWorkbookUnknownValidationError();
        case BroadcastErrorCode.RestSourceViewNameChanged:
            return Messages.broadcastSourceViewNameChangedError();
        case BroadcastErrorCode.CannotOverwrite: // User doesn't need set overwrite flag in UI
        case BroadcastErrorCode.RestCannotOverwrite:
            return Messages.broadcastServiceError();
        case BroadcastErrorCode.NotFound:
        case BroadcastErrorCode.RestNotFound:
            return Messages.broadcastNotFound();
        case BroadcastErrorCode.ServiceError:
        case BroadcastErrorCode.RestServiceError:
            return Messages.broadcastServiceError();
        default:
            return Messages.broadcastError();
    }
}

function extractErrorCodeAndMetaData(error: RestApiErrorResponse | IError): { code: number, limit: string } {
    let code: number;
    let limit: string;

    if (FeatureFlags.broadcastUIRest) {
        const RestApiError = error as RestApiErrorResponse;
        code = parseInt(RestApiError?.data?.error?.code);
        // TODO: cleanup restapi response
        if(!code) {
          code = RestApiError?.status;
        }
        limit = extractLimitValueFromError(RestApiError?.data?.error?.detail);
    } else {
        const webApiError = error as IError;
        code = webApiError?.code;
        limit = webApiError?.errorMetaData?.detailLimit;
    }
    return { code, limit };
}

function extractLimitValueFromError(error: string): string {
    const limitMatch = error?.match(/limit:\s*(\d+)/i);
    return limitMatch ? limitMatch[1] : '';
}
